import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';

import { KeyboardKeys } from '@/shared/utils/navigation';

import { cn } from '@/lib/utils';

interface Props extends React.PropsWithChildren {
	containerClassName?: string;
	className?: string;
	buttonsClassName?: string;
	leftButtonClassName?: string;
	rightButtonClassName?: string;
}

function InvisibleScroller({
	children,
	containerClassName,
	className,
	buttonsClassName,
	leftButtonClassName,
	rightButtonClassName,
}: Props) {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const [showLeftArrow, setShowLeftArrow] = useState(false);
	const [showRightArrow, setShowRightArrow] = useState(false);
	const [isHovered, setIsHovered] = useState(false);

	const updateArrowVisibility = useCallback(() => {
		const container = scrollContainerRef.current;
		if (container) {
			const buffer = 4;
			const hasOverflow = container.scrollWidth > container.clientWidth;
			setShowLeftArrow(hasOverflow && container.scrollLeft > buffer);
			setShowRightArrow(
				hasOverflow && container.scrollLeft < container.scrollWidth - container.clientWidth - buffer
			);
		}
	}, []);

	const handleScroll = useCallback(
		(direction: 'left' | 'right') => {
			const container = scrollContainerRef.current;
			if (container) {
				const scrollAmount = 0.8 * container.clientWidth;
				const newScrollPosition =
					direction === 'left'
						? container.scrollLeft - scrollAmount
						: container.scrollLeft + scrollAmount;

				container.scrollTo({
					left: newScrollPosition,
					behavior: 'smooth',
				});

				updateArrowVisibility();
			}
		},
		[updateArrowVisibility]
	);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (isHovered) {
				if (e.key === KeyboardKeys.ArrowLeft) {
					handleScroll('left');
				} else if (e.key === KeyboardKeys.ArrowRight) {
					handleScroll('right');
				}
			}
		},
		[isHovered]
	);

	useEffect(() => {
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isHovered]);

	useEffect(() => {
		updateArrowVisibility();
		window.addEventListener('resize', updateArrowVisibility);
		return () => window.removeEventListener('resize', updateArrowVisibility);
	}, []);

	return (
		<div
			className={cn('relative w-full', containerClassName)}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			{showLeftArrow && (
				<Button
					variant="ghost"
					size="icon"
					className={cn(
						'absolute left-0 top-1/2 z-20 -translate-y-1/2 bg-gradient-to-r from-white via-white to-transparent hover:bg-white',
						buttonsClassName,
						leftButtonClassName
					)}
					onClick={() => handleScroll('left')}
				>
					<ChevronLeft className="h-5 w-5" />
				</Button>
			)}
			<div
				ref={scrollContainerRef}
				className={cn(
					'no-scrollbar flex items-center justify-start gap-2 overflow-x-auto',
					className
				)}
				onScroll={updateArrowVisibility}
			>
				{children}
			</div>
			{showRightArrow && (
				<Button
					variant="ghost"
					size="icon"
					className={cn(
						'absolute right-0 top-1/2 z-20 -translate-y-1/2 bg-gradient-to-l from-white via-white to-transparent hover:bg-white',
						buttonsClassName,
						rightButtonClassName
					)}
					onClick={() => handleScroll('right')}
				>
					<ChevronRight className="h-5 w-5" />
				</Button>
			)}
		</div>
	);
}

export default InvisibleScroller;
